/**
 * Auto-reply rule schemas -- client-side validation for /api/auto-reply/*.
 *
 * An auto-reply rule maps the normalized first word of an inbound MO to a
 * static response. When the word matches, the engine in webhooks-mo.ts
 * sends response_body back to the sender. Rules are per-user, per-channel.
 *
 * Server is authoritative. The client validates upfront for fast UX
 * feedback; the backend re-validates and -- crucially -- re-normalizes
 * trigger_word server-side, so the stored value always matches what the
 * webhook hot path compares against.
 *
 * Schemas exported (request bodies):
 *   - createAutoReplyRuleSchema   POST   /api/auto-reply
 *   - updateAutoReplyRuleSchema   PATCH  /api/auto-reply/:id
 *   - listAutoReplyRequestSchema  GET    /api/auto-reply?...
 *
 * Threat model V253-V260 (see docs/auto-reply-design.md):
 *   V255 cross-tenant -- every query/mutation filters by user_id (route).
 *   V257 no-injection  -- response_body is static text; length-bounded
 *        here, no variable substitution from MO content.
 *
 * Types inferred via z.infer<typeof ...>.
 */

import { z } from "zod";

// Channels an auto-reply rule can target. "sms" today; the column and
// schema are multi-channel ready (Migration 0034 inbound_messages.channel).
export const autoReplyChannelSchema = z.enum(["sms", "viber", "telegram"]);
export type AutoReplyChannel = z.infer<typeof autoReplyChannelSchema>;

// Bounds. trigger_word is a single word; response_body is one SMS-scale
// message. Kept generous but finite (anti-DoS, V257).
export const AUTO_REPLY_LIMITS = {
  TRIGGER_WORD_MAX: 64,
  RESPONSE_BODY_MAX: 1000,
  PAGE_SIZE_MAX: 100,
} as const;

// trigger_word: a single token, no whitespace. The server re-normalizes
// (lowercase + strip diacritics) before storing; this schema only guards
// shape + length so obviously-bad input is rejected early.
const triggerWordSchema = z
  .string()
  .trim()
  .min(1, "Trigger word is required")
  .max(AUTO_REPLY_LIMITS.TRIGGER_WORD_MAX, "Trigger word is too long")
  .refine((v) => !/\s/.test(v), "Trigger word must be a single word");

const responseBodySchema = z
  .string()
  .trim()
  .min(1, "Response body is required")
  .max(AUTO_REPLY_LIMITS.RESPONSE_BODY_MAX, "Response body is too long");

export const createAutoReplyRuleSchema = z.object({
  channel: autoReplyChannelSchema.default("sms"),
  trigger_word: triggerWordSchema,
  response_body: responseBodySchema,
  enabled: z.boolean().default(true),
  also_blocklist: z.boolean().default(false),
  // 4.4d: WHO the reply speaks as. Absent = the computed default
  // (arrival short code when dedicated, legacy INFO otherwise).
  // Shape-only here; the REAL gate is checkSenderAllowed at save+send.
  sender_id: z.string().trim().min(1).max(16).optional(),
});
export type CreateAutoReplyRuleInput = z.infer<
  typeof createAutoReplyRuleSchema
>;

// PATCH -- every field optional; at least one must be present.
export const updateAutoReplyRuleSchema = z
  .object({
    trigger_word: triggerWordSchema.optional(),
    response_body: responseBodySchema.optional(),
    enabled: z.boolean().optional(),
    also_blocklist: z.boolean().optional(),
    // null = clear back to the computed default.
    sender_id: z.string().trim().min(1).max(16).nullable().optional(),
  })
  .refine(
    (v) => Object.keys(v).length > 0,
    "At least one field must be provided",
  );
export type UpdateAutoReplyRuleInput = z.infer<
  typeof updateAutoReplyRuleSchema
>;

export const listAutoReplyRequestSchema = z.object({
  channel: autoReplyChannelSchema.optional(),
});
export type ListAutoReplyRequest = z.infer<
  typeof listAutoReplyRequestSchema
>;

// Response DTO -- one rule as returned by the API.
// Observability counters over the last 24h (V260) -- how many MOs got
// a reply vs were suppressed by a protection rule, so the user never
// loses messages silently.
export type AutoReplyStats = {
  replied: number;
  suppressed_rate_cap: number;
  suppressed_killswitch: number;
};

export type AutoReplyRuleDto = {
  id: string;
  sender_id: string | null;
  channel: AutoReplyChannel;
  trigger_word: string;
  response_body: string;
  enabled: boolean;
  also_blocklist: boolean;
  created_at: number;
  updated_at: number;
};
